import { TimeslotDisplayDateComponents } from '@Generated/back-office-client/src'
import {
  addDays,
  format,
  isSameDay,
  isSaturday,
  isSunday,
  isWithinInterval,
  startOfDay,
} from 'date-fns'
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz'

export const convertDateToUTC = (date: Date) => {
  return new Date(
    Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
      date.getUTCHours(),
      0,
      0,
    ),
  )
}

export const formatDateToDayString = (date: Date, includeYear: boolean = false) => {
  return format(date, `EEEE, MMM d${includeYear ? ', yyyy' : ''}`)
}

export const formatWeekString = (startDate: Date, endDate: Date) => {
  const sameMonth = startDate.getMonth() === endDate.getMonth()
  return `${format(startDate, 'MMMM d')}-${format(endDate, sameMonth ? 'd' : 'MMMM d')}`
}

export const formatDateComponentsToDayString = (components: TimeslotDisplayDateComponents) => {
  return `${components.dayOfWeek}, ${components.monthShort} ${components.dayOfMonth}`
}

export const formatDateToMonthDayYearString = (date: Date, timezone?: string) => {
  return formatInTimeZone(date, timezone ?? 'UTC', 'MM/dd/yyyy')
}

export const formatDateToLongMonthDayYearString = (date: Date, timezone?: string) => {
  return formatInTimeZone(date, timezone ?? 'UTC', 'MMMM d, yyyy')
}

export const formatDateToMonthDayShortYearString = (date: Date, timezone?: string) => {
  return formatInTimeZone(date, timezone ?? 'UTC', 'MM/dd/yy')
}

export const formatDateToTimeZone = (date: Date, timezone?: string) => {
  return formatInTimeZone(date, timezone ?? 'UTC', 'PP')
}

export const formatDateToTimeZoneTime = (date: Date, timezone?: string) => {
  return formatInTimeZone(date, timezone ?? 'UTC', 'p')
}

// Format a Date as the wall-clock value for an `<input type="datetime-local">` in the given timezone.
export const toDateTimeLocalInputValue = (timezone: string, date?: Date | null): string => {
  if (!date) {
    return ''
  }
  return formatInTimeZone(date, timezone, "yyyy-MM-dd'T'HH:mm")
}

// Parse a `<input type="datetime-local">` value (no zone) as wall-clock in the given timezone, return UTC ISO string.
export const fromDateTimeLocalInputValue = (timezone: string, local: string): string => {
  if (!local) {
    return ''
  }
  return fromZonedTime(local, timezone).toISOString()
}

export const convertDateToIsoDate = (date: Date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export const getDatesBetweenRange = (startDate: Date, endDate: Date) => {
  let currentDate = startDate
  const dateRange = [convertDateToIsoDate(currentDate)]

  while (!isSameDay(endDate, currentDate)) {
    currentDate = addDays(currentDate, 1)
    dateRange.push(convertDateToIsoDate(currentDate))
  }

  return dateRange
}

export const getTodayAsUTC = () => {
  return convertDateToUTC(new Date())
}

export const getNearestDayOfWeek = (
  date: Date,
  dateFn: (date: Date) => boolean,
  future: boolean,
) => {
  let currentDate = date
  while (!dateFn(currentDate)) {
    currentDate = addDays(currentDate, future ? 1 : -1)
  }

  return currentDate
}

export const getNearestFutureWeekday = (date: Date) => {
  if (isSaturday(date)) {
    return addDays(date, 2)
  } else if (isSunday(date)) {
    return addDays(date, 1)
  }

  return date
}

export const isTodayVisible = (startDate: Date, endDate: Date) => {
  return isWithinInterval(startOfDay(getTodayAsUTC()), {
    start: startOfDay(startDate),
    end: startOfDay(endDate),
  })
}

export const getLogoutParams = () => {
  return {
    logoutParams: {
      returnTo: `${window.location.protocol}//${window.location.host}`,
    },
  }
}

export const formatExceptionHoursToMatchClaimCenter = (
  startTime: Date,
  endTime: Date,
  claimCenterStartHour: number,
  claimCenterEndHour: number,
) => {
  if (startTime.getHours() + 1 < claimCenterStartHour) {
    startTime.setHours(claimCenterStartHour)
  }

  if (endTime.getHours() + 1 > claimCenterEndHour) {
    endTime.setHours(claimCenterEndHour)
  }

  return { startTime, endTime }
}

export const formatToTimeDisplay = (date: Date) => {
  return format(date, 'h:mmaaa').toLowerCase()
}

export const formatToCurrencyDisplay = (amountInCents: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amountInCents / 100)
}

// Helper to filter out TSOA-generated metadata values
export const getRealEnumValues = <T extends string>(enumObj: Record<string, T>): T[] => {
  return Object.values(enumObj).filter(
    (value) =>
      !value.startsWith('_') && // Exclude _11184809
      value !== 'unknown_default_open_api' && // Exclude unknown_default_open_api
      isNaN(Number(value)), // Exclude numeric string values like '11184809'
  )
}

/**
 * Formats enum values to human-readable labels
 * Replaces underscores and dashes with spaces, applies title case
 * e.g., "FAST_PLAY" → "Fast Play"
 * e.g., "TICKET_COST_MULTIPLIER" → "Ticket Cost Multiplier"
 */
export const formatEnumLabel = (value: string): string => {
  if (!value) {
    return ''
  }

  return value
    .replace(/[_-]/g, ' ')
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}
